<template>
  <component
    :is="as"
    :class="computedClass"
    v-bind="$attrs"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    @mouseup="onMouseUp"
    @mouseover="onMouseOver"
  >
    <slot />
  </component>
</template>

<script lang="ts">
import { PropType } from 'vue'
import { mapMutations, mapState } from 'vuex'

import { ChecklistDroppableAreaVariant, ChecklistSection } from '@/project/checklist/types'
import { SET_CHECKLIST_DRAG_OVER_SECTION } from '@/store/modules/checklist/mutation_types'

export default {
  name: 'ChecklistTableDroppableArea',
  emits: ['mouseenter', 'mouseleave', 'mouseUpDrop'],
  props: {
    as: {
      type: [String, Object],
      default: 'div',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String as PropType<ChecklistDroppableAreaVariant>,
      default: '',
    },
    section: {
      type: Object as PropType<ChecklistSection>,
      default: null,
    },
  },
  data() {
    return {
      isActive: false,
    }
  },
  computed: {
    ...mapState('checklist', ['checklistDragItem']),
    computedClass() {
      return {
        [this.variant]: !!this.variant, // 'after-section', 'after-subsection', 'after-task'
        'dragging-over': this.checklistDragItem,
        'drop-zone-active': this.isActive,
        'drop-disabled': this.disabled && this.checklistDragItem,
      }
    },
  },
  watch: {
    checklistDragItem(newVal) {
      if (!newVal) {
        this.isActive = false
      }
    },
  },
  methods: {
    ...mapMutations('checklist', [SET_CHECKLIST_DRAG_OVER_SECTION]),

    onMouseEnter(event: MouseEvent) {
      if (this.section) {
        this.SET_CHECKLIST_DRAG_OVER_SECTION(this.section)
      } else {
        this.SET_CHECKLIST_DRAG_OVER_SECTION(null)
      }
      event.stopPropagation()
      event.preventDefault()

      this.$emit('mouseenter')

      if (this.checklistDragItem) {
        this.isActive = true
      }
    },
    onMouseOver() {
      if (this.checklistDragItem) {
        this.isActive = true
      }
    },
    onMouseLeave() {
      if (this.section) {
        this.SET_CHECKLIST_DRAG_OVER_SECTION(null)
      }

      this.$emit('mouseleave')
      this.isActive = false
    },
    onMouseUp() {
      if (this.disabled) return

      if (this.checklistDragItem && this.isActive) {
        this.$emit('mouseUpDrop')
      }

      this.isActive = false
    },
  },
}
</script>

<style scoped lang="scss">
.after-subsection {
  position: relative;
  z-index: 2;
  height: 0px;
  &.dragging-over {
    height: 8px;
    transition: height 0.2s ease-in-out;
  }
}

.after-task {
  z-index: 2;
  height: 4px;
  grid-column: 1 / -1;
  margin-top: -4px;
}

.drop-zone-active {
  transition: all 0.1s ease-in-out;
  background-color: rgb(var(--v-theme-darkblue-50)) !important;
  overflow: hidden;
  cursor: grabbing;

  & * {
    cursor: grabbing;
    pointer-events: none !important;
  }

  &.after-section {
    border-radius: 8px;
    outline: 1px solid rgb(var(--v-theme-darkblue-500));
    outline-offset: -1px;
  }

  &.after-subsection {
    border: 1px solid rgb(var(--v-theme-darkblue-500));
    border-radius: 4px;
  }

  &.after-task {
    background-color: rgb(var(--v-theme-darkblue-500)) !important;
  }
}

.drop-disabled {
  cursor: not-allowed !important;
  outline: none !important;
  color: rgb(var(--v-theme-grey-400)) !important;
  background-color: rgb(var(--v-theme-grey-25)) !important;
}
</style>
